'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import { useAuth } from '@/components/auth-provider';
// The My Apps shelf query, shared rather than re-derived. See `fetchMineApps`
// below for why the Mine scope cannot read the posts feed.
import {
  MY_LISTINGS_URL,
  mineShelfUrl,
  type MyListing,
} from '@/components/workspace/discover/discover-data';

export type ContentType = 'all' | 'agents' | 'humans' | 'channels' | 'posts' | 'tags' | 'chats' | 'widgets';

const ALL_SEARCHABLE_TYPES: ContentType[] = ['agents', 'posts', 'widgets', 'channels', 'humans', 'tags'];
export type Scope = 'everyone' | 'mine' | 'following' | 'saved';
export type ViewMode = 'grid' | 'list';

export interface DiscoverFilters {
  query: string;
  types: ContentType[];
  scopes: Scope[];
  sort: string;
  timeRange?: 'hour' | 'day' | 'week' | 'month' | 'year';
  channels: string[];
  tags: string[];
  hasMedia?: boolean;
  authorType?: 'human' | 'agent';
  /**
   * Narrow post results to a single backing content_type — e.g.
   * `'widget'` to surface only widget posts in the Zap → Discover
   * tab. Plumbed into the discovery API as `content_type=<value>`.
   * Posts API filters by their attached content's type; ignored by
   * non-post types.
   */
  contentSubtype?: string;
}

export interface DiscoverPreset {
  title: string;
  subtitle?: string;
  type?: ContentType;
  types?: ContentType[];
  scope?: Scope;
  scopes?: Scope[];
  tags?: string[];
  channels?: string[];
  channel?: string;
  showFeatured?: boolean;
  hideTypeBar?: boolean;
  hideScopeBar?: boolean;
  allowedTypes?: ContentType[];
  quickTags?: string[];
  /** Preset for `DiscoverFilters.contentSubtype` (e.g. `'widget'`). */
  contentSubtype?: string;
}

export interface FeaturedData {
  agents?: { items: any[]; curatedCount: number; hasMore: boolean };
  channels?: { items: any[]; curatedCount: number; hasMore: boolean };
  posts?: { items: any[]; curatedCount: number; hasMore: boolean };
  users?: { items: any[]; curatedCount: number; hasMore: boolean };
  tags?: { items: any[]; curatedCount: number; hasMore: boolean };
  marketplace?: { items: any[]; curatedCount: number; hasMore: boolean };
}

const DEFAULT_SORT: Record<ContentType, string> = {
  all: 'trending',
  posts: 'trending',
  agents: 'reputation',
  channels: 'trending',
  humans: 'reputation',
  tags: 'popular',
  chats: 'new',
  // Widgets share posts' trending heuristic — install_count is
  // collected but not yet wired into a sort option (we ship the
  // marketplace on `hot` and revisit ordering once usage stats exist).
  widgets: 'hot',
};

function readViewModeFromStorage(): ViewMode {
  try {
    const stored = localStorage.getItem('discover-view-mode');
    if (stored === 'grid' || stored === 'list') return stored;
  } catch { /* ignore */ }
  return window.innerWidth < 768 ? 'list' : 'grid';
}

function resolvePresetChannels(preset?: DiscoverPreset): string[] {
  if (preset?.channels?.length) return preset.channels;
  if (preset?.channel) return [preset.channel];
  return [];
}

function resolvePresetTypes(preset?: DiscoverPreset): ContentType[] {
  if (preset?.types?.length) return preset.types;
  if (preset?.type && preset.type !== 'all') return [preset.type];
  return [];
}

function resolvePresetScopes(preset?: DiscoverPreset): Scope[] {
  if (preset?.scopes?.length) return preset.scopes;
  if (preset?.scope && preset.scope !== 'everyone') return [preset.scope];
  return [];
}

function buildDefaultFilters(preset?: DiscoverPreset, searchParams?: URLSearchParams): DiscoverFilters {
  const tagsParam = searchParams?.get('tags');
  const channelsParam = searchParams?.get('channels');
  const typesParam = searchParams?.get('types');
  const scopesParam = searchParams?.get('scopes');
  return {
    query: searchParams?.get('q') || '',
    types: typesParam ? typesParam.split(',') as ContentType[] : resolvePresetTypes(preset),
    scopes: scopesParam ? scopesParam.split(',') as Scope[] : resolvePresetScopes(preset),
    sort: searchParams?.get('sort') || DEFAULT_SORT[preset?.type || 'all'],
    timeRange: (searchParams?.get('time') as DiscoverFilters['timeRange']) || undefined,
    channels: channelsParam ? channelsParam.split(',') : resolvePresetChannels(preset),
    tags: tagsParam ? tagsParam.split(',') : (preset?.tags || []),
    hasMedia: searchParams?.get('media') === 'true' ? true : undefined,
    authorType: (searchParams?.get('author') as 'human' | 'agent') || undefined,
    contentSubtype: searchParams?.get('content_type') || preset?.contentSubtype,
  };
}

function effectiveType(types: ContentType[]): ContentType {
  if (types.length === 0) return 'all';
  if (types.length === 1) return types[0];
  return 'all';
}

function isSimpleQuery(
  filters: DiscoverFilters,
  presetTags: string[],
  presetChannels: string[],
  presetScopes: Scope[],
  presetContentSubtype?: string,
): boolean {
  return !filters.query &&
    !filters.timeRange &&
    !filters.hasMedia &&
    !filters.authorType &&
    // `contentSubtype` narrows the post stream by a specific
    // content_type (currently only `widget` is wired). Treating it as
    // "filters present" prevents the `/api/featured/trending` prepend
    // from leaking subtype-mismatched curated posts back into a
    // narrowed feed — see ADR 0018.
    (!filters.contentSubtype || filters.contentSubtype === presetContentSubtype) &&
    (filters.channels.length === 0 || JSON.stringify(filters.channels) === JSON.stringify(presetChannels)) &&
    (filters.tags.length === 0 || JSON.stringify(filters.tags) === JSON.stringify(presetTags)) &&
    (JSON.stringify(filters.scopes) === JSON.stringify(presetScopes) || filters.scopes.length === 0);
}

/**
 * Exported for tests — pins the invariant that the Widgets filter
 * intentionally does NOT inherit any curated `/api/featured/trending`
 * prepend. See ADR 0018.
 */
export const CONTENT_TYPE_TO_TRENDING_SECTION: Record<string, string> = {
  posts: 'posts',
  agents: 'agents',
  channels: 'channels',
  humans: 'users',
  // Widgets DELIBERATELY have no curated trending section. The
  // `/api/featured/trending` endpoint sources its `posts` section from
  // pinned hero rows (see `scripts/seed-pinned-posts.ts`) that aren't
  // filtered by `content_type='widget'`, so reusing it polluted the
  // Widgets marketplace with "Welcome to Robutler"-style hero posts —
  // see ADR 0018. Widget results come straight from `/api/posts?content_type=widget`
  // with no prepend so system widgets (pyodide, ssh, daemon) and
  // user-published HTML widgets land first.
};

const SECTION_API_MAP: Record<string, string> = {
  agents: '/api/discovery/agents?type=agent',
  channels: '/api/channels',
  posts: '/api/posts',
  users: '/api/discovery/agents?type=human',
  tags: '/api/tags',
};

export function useDiscover(preset?: DiscoverPreset) {
  const { user } = useAuth();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [filters, setFilters] = useState<DiscoverFilters>(() => buildDefaultFilters(preset, searchParams));

  const [viewMode, setViewModeState] = useState<ViewMode>('grid');
  const [featured, setFeatured] = useState<FeaturedData | null>(null);

  useEffect(() => {
    setViewModeState(readViewModeFromStorage());
  }, []);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [featuredError, setFeaturedError] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);

  // Per-section featured pagination
  const [featuredOffsets, setFeaturedOffsets] = useState<Record<string, number>>({});
  const [sectionLoading, setSectionLoading] = useState<Record<string, boolean>>({});

  // Multi-type results state
  const [multiResults, setMultiResults] = useState<Record<ContentType, { items: any[]; hasMore: boolean; offset: number }>>({} as any);
  const [multiLoading, setMultiLoading] = useState<Record<ContentType, boolean>>({} as any);

  const abortRef = useRef<AbortController | null>(null);
  const isInternalUrlUpdate = useRef(false);

  const showFeatured = preset?.showFeatured !== false;

  const presetTags = useMemo(() => preset?.tags || [], [preset?.tags]);
  const presetChannels = useMemo(() => resolvePresetChannels(preset), [preset?.channels, preset?.channel]);
  const presetTypes = useMemo(() => resolvePresetTypes(preset), [preset?.types, preset?.type]);
  const presetScopes = useMemo(() => resolvePresetScopes(preset), [preset?.scopes, preset?.scope]);

  const hasActiveFilters = !!filters.query || !!filters.timeRange || !!filters.hasMedia ||
    !!filters.authorType ||
    // Same rationale as `isSimpleQuery`: a content_type narrowing
    // (e.g. widget) IS a meaningful filter — treating it as inactive
    // would re-enable the featured-trending hero prepend and leak
    // non-widget posts back into the Widgets marketplace.
    (!!filters.contentSubtype && filters.contentSubtype !== preset?.contentSubtype) ||
    (filters.channels.length > 0 && JSON.stringify(filters.channels) !== JSON.stringify(presetChannels)) ||
    (filters.tags.length > 0 && JSON.stringify(filters.tags) !== JSON.stringify(presetTags)) ||
    (filters.types.length > 0 && JSON.stringify(filters.types) !== JSON.stringify(presetTypes)) ||
    JSON.stringify(filters.scopes) !== JSON.stringify(presetScopes);

  const isFeaturedMode = showFeatured && !hasActiveFilters;
  const isMultiTypeMode = !isFeaturedMode && (filters.types.length > 1 || filters.types.length === 0);

  const activeFilterCount =
    filters.tags.length +
    filters.channels.length +
    filters.types.length +
    filters.scopes.length +
    (filters.timeRange ? 1 : 0) +
    (filters.hasMedia ? 1 : 0) +
    (filters.authorType ? 1 : 0);

  const setViewMode = useCallback((mode: ViewMode) => {
    setViewModeState(mode);
    try { localStorage.setItem('discover-view-mode', mode); } catch { /* ignore */ }
  }, []);

  const updateFilter = useCallback(<K extends keyof DiscoverFilters>(key: K, value: DiscoverFilters[K]) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setMultiResults({} as any);
  }, []);

  const toggleType = useCallback((t: ContentType) => {
    setFilters(prev => {
      const current = prev.types;
      const next = current.includes(t) ? current.filter(x => x !== t) : [...current, t];
      return { ...prev, types: next, sort: DEFAULT_SORT[effectiveType(next)] || 'trending' };
    });
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setMultiResults({} as any);
  }, []);

  const toggleTag = useCallback((tag: string) => {
    setFilters(prev => {
      const current = prev.tags;
      const next = current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag];
      return { ...prev, tags: next };
    });
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setMultiResults({} as any);
  }, []);

  const toggleChannel = useCallback((ch: string) => {
    setFilters(prev => {
      const current = prev.channels;
      const next = current.includes(ch) ? current.filter(c => c !== ch) : [...current, ch];
      return { ...prev, channels: next };
    });
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setMultiResults({} as any);
  }, []);

  const toggleScope = useCallback((s: Scope) => {
    setFilters(prev => {
      const current = prev.scopes;
      const next = current.includes(s) ? current.filter(x => x !== s) : [...current, s];
      return { ...prev, scopes: next };
    });
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setMultiResults({} as any);
  }, []);

  const clearFilter = useCallback((key: keyof DiscoverFilters) => {
    setFilters(prev => {
      const next = { ...prev };
      if (key === 'tags') next.tags = [];
      else if (key === 'channels') next.channels = [];
      else if (key === 'types') next.types = [];
      else if (key === 'scopes') next.scopes = [];
      else (next as any)[key] = undefined;
      return next;
    });
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setMultiResults({} as any);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters(prev => ({
      ...prev,
      query: '',
      types: [],
      scopes: [],
      timeRange: undefined,
      channels: [],
      tags: [],
      hasMedia: undefined,
      authorType: undefined,
    }));
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setMultiResults({} as any);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(buildDefaultFilters(preset));
    setResults([]);
    setOffset(0);
    setHasMore(false);
    setMultiResults({} as any);
  }, [preset]);

  const searchParamsStr = searchParams.toString();
  useEffect(() => {
    if (isInternalUrlUpdate.current) {
      isInternalUrlUpdate.current = false;
      return;
    }
    if (!searchParamsStr) {
      resetFilters();
    } else {
      setFilters(buildDefaultFilters(preset, searchParams));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParamsStr, resetFilters, preset]);

  // Sync filters to URL
  const prevUrlRef = useRef<string>('');
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.query) params.set('q', filters.query);
    if (filters.types.length > 0 && JSON.stringify(filters.types) !== JSON.stringify(presetTypes)) {
      params.set('types', filters.types.join(','));
    }
    if (filters.scopes.length > 0 && JSON.stringify(filters.scopes) !== JSON.stringify(presetScopes)) {
      params.set('scopes', filters.scopes.join(','));
    }
    if (filters.sort !== DEFAULT_SORT[effectiveType(filters.types)]) params.set('sort', filters.sort);
    if (filters.timeRange) params.set('time', filters.timeRange);
    if (filters.channels.length > 0 && JSON.stringify(filters.channels) !== JSON.stringify(presetChannels)) {
      params.set('channels', filters.channels.join(','));
    }
    if (filters.tags.length > 0 && JSON.stringify(filters.tags) !== JSON.stringify(presetTags)) {
      params.set('tags', filters.tags.join(','));
    }
    if (filters.hasMedia) params.set('media', 'true');
    if (filters.authorType) params.set('author', filters.authorType);
    if (filters.contentSubtype && filters.contentSubtype !== preset?.contentSubtype) {
      params.set('content_type', filters.contentSubtype);
    }

    const str = params.toString();
    const newUrl = str ? `${pathname}?${str}` : pathname;
    if (newUrl !== prevUrlRef.current) {
      prevUrlRef.current = newUrl;
      isInternalUrlUpdate.current = true;
      window.history.replaceState(null, '', newUrl);
    }
  }, [filters, pathname, presetTags, presetChannels, presetTypes, presetScopes]);

  // Fetch featured content
  const fetchFeatured = useCallback((signal?: AbortSignal) => {
    setFeaturedLoading(true);
    setFeaturedError(false);

    fetch('/api/featured/trending?sections=agents,channels,posts,users,tags&limit=12', { signal })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        for (const key of ['agents', 'channels', 'posts', 'users', 'tags'] as const) {
          if (data[key]?.items) {
            const seen = new Set<string>();
            data[key].items = data[key].items.filter((item: any) => {
              if (seen.has(item.id)) return false;
              seen.add(item.id);
              return true;
            });
          }
        }
        if (data.posts?.items) {
          data.posts.items.sort((a: any, b: any) => {
            const aMedia = a.mediaType ? 1 : 0;
            const bMedia = b.mediaType ? 1 : 0;
            return bMedia - aMedia;
          });
        }
        setFeatured(data);
        const offsets: Record<string, number> = {};
        for (const key of ['agents', 'channels', 'posts', 'users', 'tags'] as const) {
          offsets[key] = data[key]?.items?.length || 0;
        }
        setFeaturedOffsets(offsets);
        setFeaturedError(false);
      })
      .catch((err) => {
        if (err?.name !== 'AbortError') setFeaturedError(true);
      })
      .finally(() => setFeaturedLoading(false));
  }, []);

  useEffect(() => {
    if (!isFeaturedMode) return;
    const ctrl = new AbortController();
    fetchFeatured(ctrl.signal);
    return () => ctrl.abort();
  }, [isFeaturedMode, fetchFeatured]);

  // Load more for a featured section (infinite horizontal scroll)
  const loadMoreFeatured = useCallback(async (section: string) => {
    if (sectionLoading[section]) return;
    const currentOffset = featuredOffsets[section] || 0;
    const apiBase = SECTION_API_MAP[section];
    if (!apiBase) return;

    setSectionLoading(prev => ({ ...prev, [section]: true }));
    try {
      const sep = apiBase.includes('?') ? '&' : '?';
      const url = `${apiBase}${sep}limit=12&offset=${currentOffset}&sort=trending`;
      const res = await fetch(url);
      if (!res.ok) return;
      const data = await res.json();

      let newItems: any[] = [];
      if (section === 'agents' || section === 'users') {
        newItems = data.agents || data.users || [];
      } else if (section === 'channels') {
        const cats = data.categories || [];
        for (const cat of cats) {
          for (const ch of cat.channels || []) newItems.push(ch);
        }
        if (newItems.length === 0) newItems = data.channels || [];
      } else if (section === 'posts') {
        newItems = data.posts || data.items || [];
      } else if (section === 'tags') {
        newItems = data.tags || [];
      }

      if (newItems.length > 0) {
        setFeatured(prev => {
          if (!prev) return prev;
          const sectionData = prev[section as keyof FeaturedData];
          if (!sectionData) return prev;
          const existingIds = new Set(sectionData.items.map((i: any) => i.id));
          const deduped = newItems.filter((i: any) => !existingIds.has(i.id));
          return {
            ...prev,
            [section]: {
              ...sectionData,
              items: [...sectionData.items, ...deduped],
              hasMore: newItems.length >= 12,
            },
          };
        });
        setFeaturedOffsets(prev => ({ ...prev, [section]: currentOffset + newItems.length }));
      }
    } catch { /* ignore */ }
    finally {
      setSectionLoading(prev => ({ ...prev, [section]: false }));
    }
  }, [featuredOffsets, sectionLoading]);

  // Fetch single-type search results, with curated prepend on simple queries
  useEffect(() => {
    if (isFeaturedMode || isMultiTypeMode) return;

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const simple = isSimpleQuery(filters, presetTags, presetChannels, presetScopes, preset?.contentSubtype);
    const effType = effectiveType(filters.types);
    const trendingSection = CONTENT_TYPE_TO_TRENDING_SECTION[effType];
    const isMarketplacePreset = preset?.showFeatured === false;
    // Mine is a shelf of the viewer's OWN apps (see `fetchMineApps`). A
    // curated marketplace prepend would stack other people's apps on top of
    // it, which is the one thing "Mine" promises not to do.
    const isMineApps = effType === 'widgets' && filters.scopes.includes('mine') && !!user?.id;

    setLoading(true);

    const fetchPromise = fetchResults(filters, 0, ctrl.signal, user?.id);

    const sectionToFetch = isMineApps ? undefined : (isMarketplacePreset ? 'marketplace' : trendingSection);
    const curatedPromise = (simple && sectionToFetch)
      ? fetch(`/api/featured/trending?sections=${sectionToFetch}&limit=50`, { signal: ctrl.signal })
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      : Promise.resolve(null);

    Promise.all([fetchPromise, curatedPromise])
      .then(([{ items, hasMore: more }, curatedData]) => {
        const resolvedSection = isMineApps ? undefined : (isMarketplacePreset ? 'marketplace' : trendingSection);
        if (curatedData && resolvedSection) {
          const sectionData = curatedData[resolvedSection];
          if (sectionData?.items?.length) {
            const curatedItems = sectionData.items.filter((i: any) => i.isCurated);
            if (curatedItems.length > 0) {
              const curatedIds = new Set(curatedItems.map((c: any) => c.id));
              items = [...curatedItems, ...items.filter((i: any) => !curatedIds.has(i.id))];
            }
          }
        }
        setResults(items);
        setHasMore(more);
        setOffset(items.length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    return () => ctrl.abort();
  }, [filters, isFeaturedMode, isMultiTypeMode, user?.id, presetTags, presetChannels, presetScopes]);

  // Fetch multi-type search results, with curated prepend on simple queries
  useEffect(() => {
    if (!isMultiTypeMode) return;

    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const simple = isSimpleQuery(filters, presetTags, presetChannels, presetScopes, preset?.contentSubtype);

    const types = filters.types.length > 0 ? filters.types
      : (user ? [...ALL_SEARCHABLE_TYPES, 'chats' as ContentType] : ALL_SEARCHABLE_TYPES);
    const newLoading: Record<ContentType, boolean> = {} as any;
    types.forEach(t => { newLoading[t] = true; });
    setMultiLoading(newLoading);

    const trendingSections = simple
      ? types.map(t => CONTENT_TYPE_TO_TRENDING_SECTION[t]).filter(Boolean)
      : [];
    const curatedPromise = trendingSections.length > 0
      ? fetch(`/api/featured/trending?sections=${trendingSections.join(',')}&limit=50`, { signal: ctrl.signal })
          .then(r => r.ok ? r.json() : null)
          .catch(() => null)
      : Promise.resolve(null);

    Promise.all([
      Promise.all(
        types.map(async (t) => {
          const singleFilters = { ...filters, types: [t] };
          try {
            const { items, hasMore: more } = await fetchResults(singleFilters, 0, ctrl.signal, user?.id);
            return { type: t, items, hasMore: more };
          } catch {
            return { type: t, items: [], hasMore: false };
          }
        }),
      ),
      curatedPromise,
    ]).then(([results, curatedData]) => {
      const newMulti: Record<ContentType, { items: any[]; hasMore: boolean; offset: number }> = {} as any;
      const newLoad: Record<ContentType, boolean> = {} as any;
      for (const r of results) {
        let items = r.items;
        if (curatedData) {
          const section = CONTENT_TYPE_TO_TRENDING_SECTION[r.type];
          const sectionData = section ? curatedData[section] : null;
          if (sectionData?.items?.length) {
            const curatedItems = sectionData.items.filter((i: any) => i.isCurated);
            if (curatedItems.length > 0) {
              const curatedIds = new Set(curatedItems.map((c: any) => c.id));
              items = [...curatedItems, ...items.filter((i: any) => !curatedIds.has(i.id))];
            }
          }
        }
        newMulti[r.type] = { items, hasMore: r.hasMore, offset: r.items.length };
        newLoad[r.type] = false;
      }
      setMultiResults(newMulti);
      setMultiLoading(newLoad);
    });

    return () => ctrl.abort();
  }, [filters, isMultiTypeMode, user?.id, presetTags, presetChannels, presetScopes]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore || isFeaturedMode || isMultiTypeMode) return;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setLoading(true);
    try {
      const { items, hasMore: more } = await fetchResults(filters, offset, ctrl.signal, user?.id);
      setResults(prev => {
        const seen = new Set(prev.map((i: any) => i.id));
        return [...prev, ...items.filter((i: any) => !seen.has(i.id))];
      });
      setHasMore(more);
      setOffset(prev => prev + items.length);
    } catch { /* ignore */ }
    setLoading(false);
  }, [filters, offset, loading, hasMore, isFeaturedMode, isMultiTypeMode, user?.id]);

  const loadMoreMulti = useCallback(async (type: ContentType) => {
    if (multiLoading[type]) return;
    const section = multiResults[type];
    if (!section || !section.hasMore) return;

    setMultiLoading(prev => ({ ...prev, [type]: true }));
    try {
      const singleFilters = { ...filters, types: [type] };
      const ctrl = new AbortController();
      const { items, hasMore: more } = await fetchResults(singleFilters, section.offset, ctrl.signal, user?.id);
      setMultiResults(prev => {
        const existing = prev[type]?.items || [];
        const seen = new Set(existing.map((i: any) => i.id));
        return {
          ...prev,
          [type]: {
            items: [...existing, ...items.filter((i: any) => !seen.has(i.id))],
            hasMore: more,
            offset: (prev[type]?.offset || 0) + items.length,
          },
        };
      });
    } catch { /* ignore */ }
    setMultiLoading(prev => ({ ...prev, [type]: false }));
  }, [filters, multiResults, multiLoading, user?.id]);

  // Build multi-type sections for the component
  const multiSections = useMemo(() => {
    if (!isMultiTypeMode) return [];
    const types = filters.types.length > 0 ? filters.types
      : (user ? [...ALL_SEARCHABLE_TYPES, 'chats' as ContentType] : ALL_SEARCHABLE_TYPES);
    return types.map(type => ({
      type,
      items: multiResults[type]?.items || [],
      hasMore: multiResults[type]?.hasMore || false,
      loading: multiLoading[type] || false,
      onLoadMore: () => loadMoreMulti(type),
    }));
  }, [isMultiTypeMode, filters.types, multiResults, multiLoading, loadMoreMulti, user]);

  return {
    filters,
    setFilters,
    updateFilter,
    toggleType,
    toggleTag,
    toggleChannel,
    toggleScope,
    clearFilter,
    clearAllFilters,
    resetFilters,
    viewMode,
    setViewMode,
    isFeaturedMode,
    isMultiTypeMode,
    hasActiveFilters,
    activeFilterCount,
    presetTags,
    presetChannels,
    featured,
    featuredLoading,
    featuredError,
    retryFeatured: fetchFeatured,
    loadMoreFeatured,
    sectionLoading,
    results,
    loading,
    hasMore,
    loadMore,
    multiSections,
    preset,
    user,
  };
}

/**
 * Discover → Apps → Mine. Reported 2026-09-06: "I created a private app and
 * looked in Discover, Mine" and it was not there, for its own owner.
 *
 * The cause was structural, not a filter that needed loosening. Every other
 * Apps result comes from `/api/posts?content_type=widget`, and that filter is
 * an EXISTS on `content.source_post_id = posts.id`: a row only qualifies once
 * a post exists for it. A post is minted at PUBLISH time (see
 * `app/api/content/[id]/publish-as-widget/route.ts`, which inserts the post
 * first and then points the content row at it), so an unpublished app has no
 * post and can never appear in the posts feed — for anyone, its owner
 * included. No amount of scope juggling on that feed reaches it.
 *
 * So Mine reads the thing itself instead of its announcement: the same My Apps
 * shelf query the workspace Discover panel already uses (`mineShelfUrl`),
 * paired with the caller's own listings so a live app and a private draft are
 * told apart. This is the pattern the Agents branch below already follows —
 * an owner-scoped endpoint rather than the public feed.
 *
 * Not a leak: both URLs resolve the owner from the SESSION. `owner=me` becomes
 * `creator_id IN (me, agents I own)` in `GET /api/content`, and the listings
 * route joins `channel_permissions` for the session user. Neither takes an
 * owner id from the client, so a tampered `userId` here buys nothing — it only
 * decides whether we ask at all.
 */
async function fetchMineApps(
  filters: DiscoverFilters,
  offset: number,
  limit: number,
  signal: AbortSignal,
): Promise<{ items: any[]; hasMore: boolean }> {
  // `/api/content` pages (page=1..); the feed offsets. Every page but the last
  // is full, so the conversion is exact for the way `loadMore` walks it.
  const page = Math.floor(offset / limit) + 1;
  const [shelf, listed] = await Promise.all([
    fetch(mineShelfUrl(page, filters.query || undefined, limit), { signal })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null),
    fetch(MY_LISTINGS_URL, { signal })
      .then((r) => (r.ok ? r.json() : null))
      .catch(() => null),
  ]);

  const listings: Record<string, MyListing> = listed?.listings ?? {};
  const rows: any[] = shelf?.items ?? [];

  const items = rows.map((row: any) => {
    const contentId = String(row.id);
    const meta = (row.metadata ?? null) as Record<string, unknown> | null;
    const listing = listings[contentId] ?? null;
    // The release post, when there is one. It is what gives a published app a
    // `/p/<id>` page and an install-attribution id; a draft has neither.
    const postId = typeof row.sourcePostId === 'string' ? row.sourcePostId : null;
    return {
      // Published apps keep their POST identity so the card's default
      // `/p/<id>` target, its dedupe key and its install attribution all stay
      // what they are everywhere else. A draft has only the content row.
      id: postId ?? contentId,
      title: row.displayName || null,
      content: (meta?.description as string) ?? null,
      // The capture thumbnail is the card's cover; without one the card falls
      // back to its gradient panel, same as a text post.
      mediaType: row.thumbnailPath ? 'image' : null,
      mediaUrl: null,
      thumbnailUrl: row.thumbnailPath ? `/api/content/${contentId}/preview` : null,
      createdAt: row.createdAt ?? undefined,
      // No maker line: the whole shelf is the viewer's own.
      author: null,
      channel: listing?.slug
        ? { id: listing.slug, name: row.displayName || '', slug: listing.slug }
        : null,
      // Drives the workspace "add app" CTA — the app to mount is the working
      // widget row, never the post.
      widgetContentId: contentId,
      widgetMetadata: meta,
      // A draft has no post, so `/p/<id>` would 404. `/content/<id>` is the
      // owner's view of the app itself.
      href: postId ? `/p/${postId}` : `/content/${contentId}`,
      // "Published" means a marketplace listing exists, the same test the
      // Apps panel and the workspace Discover shelf make.
      appUnpublished: !listing,
    };
  });

  const total = typeof shelf?.total === 'number' ? shelf.total : null;
  return {
    items,
    hasMore: total !== null ? page * limit < total : rows.length >= limit,
  };
}

/**
 * Exported for tests — the Mine-scope invariants above are worth pinning and
 * the hook itself cannot be driven without a React tree.
 */
export async function fetchResults(
  filters: DiscoverFilters,
  offset: number,
  signal: AbortSignal,
  userId?: string,
): Promise<{ items: any[]; hasMore: boolean }> {
  const type = effectiveType(filters.types);
  const limit = 20;

  // Apps + Mine is the one combination the posts feed structurally cannot
  // answer. Every other type and scope falls through unchanged.
  if (type === 'widgets' && filters.scopes.includes('mine') && userId) {
    return fetchMineApps(filters, offset, limit, signal);
  }

  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));

  if (filters.query) {
    params.set('q', filters.query);
    params.set('search', filters.query);
  }
  if (filters.sort) params.set('sort', filters.sort);
  if (filters.timeRange) params.set('time_range', filters.timeRange);
  if (filters.authorType) params.set('author_type', filters.authorType);
  if (filters.hasMedia) params.set('has_media', 'true');
  if (filters.contentSubtype) params.set('content_type', filters.contentSubtype);
  if (filters.tags.length > 0) params.set('tag', filters.tags.join(','));
  if (filters.channels.length > 0) params.set('channel', filters.channels.join(','));

  const scopes = filters.scopes;
  if (scopes.includes('mine') && userId) {
    params.set('scope_mine', userId);
  }
  if (scopes.includes('following') && userId) {
    params.set('scope_following', userId);
  }

  // Fetch saved/starred IDs when saved scope is active
  let savedIds: { users: string[]; channels: string[]; posts: string[] } | null = null;
  if (scopes.includes('saved') && userId) {
    try {
      const starredRes = await fetch('/api/users/me/starred', { signal });
      if (starredRes.ok) {
        savedIds = await starredRes.json();
      }
    } catch { /* ignore */ }

    if (savedIds) {
      let relevantIds: string[] = [];
      if (type === 'posts' || type === 'all') relevantIds = savedIds.posts || [];
      else if (type === 'agents' || type === 'humans') relevantIds = savedIds.users || [];
      else if (type === 'channels') relevantIds = savedIds.channels || [];

      const hasOtherScopes = scopes.includes('mine') || scopes.includes('following');
      if (relevantIds.length === 0 && !hasOtherScopes) {
        return { items: [], hasMore: false };
      }
      if (relevantIds.length > 0) {
        params.set(hasOtherScopes ? 'saved_ids' : 'ids', relevantIds.join(','));
      }
    }
  }

  let url = '';
  switch (type) {
    case 'posts':
      url = `/api/posts?${params.toString()}`;
      break;
    case 'widgets':
      // Marketplace flow — the same posts endpoint with a forced
      // `content_type=widget` filter. Lets system-seeded native
      // widgets (python/ssh/daemon) and user-published HTML widgets
      // show up in the same feed without an extra union layer.
      params.set('content_type', 'widget');
      url = `/api/posts?${params.toString()}`;
      break;
    case 'agents':
      params.set('type', 'agent');
      if (scopes.includes('mine') && userId) params.set('ownerId', userId);
      if (scopes.includes('following') && userId) params.set('followerId', userId);
      url = `/api/discovery/agents?${params.toString()}`;
      break;
    case 'channels':
      url = `/api/channels?${params.toString()}`;
      break;
    case 'humans':
      params.set('type', 'human');
      if (scopes.includes('following') && userId) params.set('followerId', userId);
      url = `/api/discovery/agents?${params.toString()}`;
      break;
    case 'tags':
      url = `/api/tags?${params.toString()}`;
      break;
    case 'chats':
      url = `/api/messages/search?q=${encodeURIComponent(filters.query || '')}&limit=${limit}&offset=${offset}`;
      break;
    default:
      url = `/api/posts?${params.toString()}`;
  }

  const res = await fetch(url, { signal });
  if (!res.ok) return { items: [], hasMore: false };
  const data = await res.json();

  let items: any[];
  if (type === 'posts' || type === 'all' || type === 'widgets') {
    items = (data.posts || data.items || []).map((p: any) => ({
      ...p,
      author: p.author || (p.authorUsername ? {
        id: p.authorId,
        username: p.authorUsername,
        displayName: p.authorDisplayName,
        avatarUrl: p.authorAvatarUrl,
        accountType: p.authorAccountType,
      } : null),
      channel: p.channel || (p.channelSlug ? {
        id: p.channelId,
        name: p.channelName,
        slug: p.channelSlug,
      } : null),
    }));
  } else if (type === 'agents' || type === 'humans') {
    let followedIdSet: Set<string> | null = null;
    if (userId && !scopes.includes('following')) {
      try {
        const followType = type === 'agents' ? 'agent' : 'human';
        const fRes = await fetch(`/api/users/me/following?type=${followType}`, { signal });
        if (fRes.ok) {
          const fData = await fRes.json();
          followedIdSet = new Set((fData.following || []).map((f: { id: string }) => f.id));
        }
      } catch { /* ignore */ }
    }
    items = (data.agents || data.users || []).map((u: any) => ({
      ...u,
      followerCount: u.followerCount || 0,
      postCount: u.postCount || 0,
      isFollowing: scopes.includes('following') ? true : (followedIdSet ? followedIdSet.has(u.id) : (u.isFollowing ?? undefined)),
    }));
  } else if (type === 'channels') {
    const cats = data.categories || [];
    items = [];
    for (const cat of cats) {
      for (const ch of cat.channels || []) items.push(ch);
    }
    if (items.length === 0) items = data.channels || [];
    items = items.map((ch: any) => ({
      ...ch,
      isFollowing: scopes.includes('following') ? true : (ch.isFollowing ?? undefined),
    }));
  } else if (type === 'tags') {
    items = data.tags || [];
  } else if (type === 'chats') {
    items = (data.conversations || []).map((c: any) => ({
      id: c.chatId,
      chatId: c.chatId,
      chatName: c.chatName,
      chatType: c.chatType,
      matchType: c.matchType,
      matchedContent: c.matchedContent,
      otherUsername: c.otherUsername,
      otherAvatarUrl: c.otherAvatarUrl,
      otherAccountType: c.otherAccountType,
      participantCount: c.participantCount,
      participants: c.participants,
    }));
  } else {
    items = [];
  }

  const seen = new Set<string>();
  const deduped = items.filter((item: any) => {
    const key = item.id;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return { items: deduped, hasMore: deduped.length >= limit };
}
